import type { IDropdownContextProps } from 'components/DropdownContext';
import { DropdownContext } from 'components/DropdownContext';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalLoading } from 'components/ModalLoading';
import { RequestStatus } from 'features/request/requestSlice';
import type { ProjectData } from 'gql/graphql';
import { ContractStatusEnum, ProjectMethodologyEnum, ProjectStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useDropdownContextMenuTooltipController } from 'hooks/useDropdownContextMenuTooltipController';
import { useWorkOrderChangeCreate } from 'hooks/useWorkOrderChangeCreate';
import { useWorkOrderDownload } from 'hooks/useWorkOrderDownload';
import { useWorkOrderPricesExport } from 'hooks/useWorkOrderPricesExport';
import { useDeleteBimOrder } from 'pages/Project/BimProject/hooks/useDeleteBimOrder';
import { BimWorkOrderChangeCreateOrEdit } from 'pages/Project/BimProject/WorkOrderChangeCreateOrEdit';
import { Tooltip } from 'uikit';

import type { FC } from 'react';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

type IBimWorkOrderKebabMenu = Partial<IDropdownContextProps> & {
    statusProject: ProjectStatusEnum | undefined;
    statusWorkOrder: WorkOrderStatusEnum | undefined;
    statusContract: ContractStatusEnum | undefined;
    hasActsKC2?: boolean;
    hasWorkOrderChangesValidStatus?: boolean;
    isFrameContract: boolean;
    isVersionPage?: boolean;
    orderRules?: IUseAccess['orderBim'];
    hasInProgress?: boolean;
    projectData: ProjectData | undefined | null;
    refreshProjectData: () => void;
    isWorkOrderChangeRequired?: boolean;
};

const getWorkOrderChangeTooltipMsg = ({
    hasWorkOrderChangesValidStatus,
    isFrameContract,
    isWorkOrderStatusOnFormation,
}: {
    hasWorkOrderChangesValidStatus?: boolean;
    isFrameContract: boolean;
    isWorkOrderStatusOnFormation: boolean;
}): string => {
    if (hasWorkOrderChangesValidStatus) {
        return `В составе ${isFrameContract ? 'НЗ' : 'Расчета'} содержится несогласованный Изм. к ${
            isFrameContract ? 'НЗ' : 'Расчету'
        }`;
    }

    return isWorkOrderStatusOnFormation
        ? `Создание "Изм. к ${isFrameContract ? 'НЗ' : 'Расчету'}" доступно только в статусе ${
              isFrameContract ? 'НЗ' : 'Расчета'
          } "В работе"`
        : `Отсутствуют изменения для создания Изм. к ${isFrameContract ? 'НЗ' : 'Расчету'}`;
};

export const BimWorkOrderKebabMenu: FC<IBimWorkOrderKebabMenu> = (props) => {
    const {
        statusProject,
        statusWorkOrder,
        statusContract,
        hasActsKC2,
        hasWorkOrderChangesValidStatus,
        isFrameContract,
        orderRules,
        hasInProgress,
        projectData,
        isVersionPage,
        refreshProjectData,
        isWorkOrderChangeRequired,
        ...propsDropdown
    } = props;

    const { orderId } = useParams();

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = statusProject === ProjectStatusEnum.Completed;
    const isDisabledByProjectStatus = isProjectStatusDelayed || isProjectStatusCompleted;

    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;

    const isWorkOrderStatusOnFormation = statusWorkOrder === WorkOrderStatusEnum.OnFormation;
    const isWorkOrderStatusInProgress = statusWorkOrder === WorkOrderStatusEnum.InProgress;

    const isItemWorkOrderChangeDisabled =
        hasWorkOrderChangesValidStatus ||
        !isContractStatusWork ||
        isDisabledByProjectStatus ||
        !isWorkOrderStatusInProgress ||
        isVersionPage;

    const isItemWorkOrderDeleteDisabled =
        !isContractStatusWork ||
        isDisabledByProjectStatus ||
        !isWorkOrderStatusOnFormation ||
        !orderRules?.canDelete ||
        isVersionPage;

    const { closeCreatingWorkOrderChangeHandler, openCreatingWorkOrderChangeHandler, isCreateWorkOrderChangeOpened } =
        useWorkOrderChangeCreate({
            workOrderId: Number(orderId),
        });

    const {
        orderDeleteBtnTitle,
        deleteBimOrderHandler,
        DeleteOrderErrorHandler,
        DeleteOrderConfirmDialog,
        orderRemoveTooltipText,
    } = useDeleteBimOrder({
        workOrderId: Number(orderId),
    });

    const {
        isSubmittedDownload,
        workOrderAsExcelData,
        workOrderAsExcelStatus,
        workOrderAsExcelError,
        workOrderAsExcelAbort,
        handleDownloadWorkOrder,
        handleCloseModalErrorDownload,
        handleCreateReportDownload,
    } = useWorkOrderDownload({
        workOrderId: Number(orderId),
        instanceId: 'fromPage',
        methodology: ProjectMethodologyEnum.Bim,
        workOrderStatus: statusWorkOrder,
    });

    const { isLoadingBimExportWorkOrderPrices, handleBimExportWorkOrderPrices, exportWorkOrderPricesAbort } =
        useWorkOrderPricesExport({ workOrderId: Number(orderId) });

    const isLoadingWorkOrder = useMemo(
        () => workOrderAsExcelStatus === RequestStatus.FETCHING,
        [workOrderAsExcelStatus],
    );

    const menuItems = useMemo(
        () => [
            ...(isFrameContract
                ? [
                      {
                          label: 'Создать Изм. к НЗ',
                          action: openCreatingWorkOrderChangeHandler,
                          id: 1,
                          groupId: 1,
                          disabled: isItemWorkOrderChangeDisabled,
                          /*
                           * RIM-7118 - убираем проверку на наличие актов (!hasActsKC2)
                           */
                          haveTooltip:
                              (hasWorkOrderChangesValidStatus || isWorkOrderStatusOnFormation) && !isVersionPage,
                          tooltipName: 'cantCreateWorkOrderChange',
                          tooltipMsg: getWorkOrderChangeTooltipMsg({
                              hasWorkOrderChangesValidStatus,
                              isFrameContract,
                              isWorkOrderStatusOnFormation,
                          }),
                      },
                  ]
                : []),
            {
                id: 2,
                groupId: 1,
                label: `Выгрузить ${isFrameContract ? 'Наряд-Заказ' : 'Расчет по Договору'}`,
                action: handleDownloadWorkOrder,
                disabled: !orderRules?.canExportExcel,
            },
            {
                id: 3,
                groupId: 1,
                label: `Выгрузить накопительную ведомость по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                disabled: isWorkOrderStatusOnFormation || !orderRules?.canExportExcel || !hasInProgress,
                haveTooltip: isWorkOrderStatusOnFormation,
                tooltipName: 'cantDownloadByOrderStatus',
                tooltipMsg: `В статусе "На формировании" недоступна выгрузка накопительной по ${
                    isFrameContract ? 'НЗ' : 'Расчету'
                }`,
                action: handleBimExportWorkOrderPrices,
            },
            {
                id: 4,
                groupId: 2,
                label: orderDeleteBtnTitle,
                disabled: isItemWorkOrderDeleteDisabled,
                action: deleteBimOrderHandler,
                haveTooltip: !isWorkOrderStatusOnFormation && !isVersionPage,
                tooltipName: 'cantRemoveOrderByOrderStatus',
                tooltipMsg: orderRemoveTooltipText,
            },
        ],
        [
            isFrameContract,
            openCreatingWorkOrderChangeHandler,
            isItemWorkOrderChangeDisabled,
            hasWorkOrderChangesValidStatus,
            isWorkOrderStatusOnFormation,
            handleDownloadWorkOrder,
            orderRules?.canExportExcel,
            hasInProgress,
            handleBimExportWorkOrderPrices,
            orderDeleteBtnTitle,
            isItemWorkOrderDeleteDisabled,
            deleteBimOrderHandler,
            isVersionPage,
            orderRemoveTooltipText,
        ],
    );

    const workOrderKebabMenuGroups = useMemo(
        () => [
            {
                id: 1,
            },
            {
                id: 2,
            },
        ],
        [],
    );

    const { getMenuItemAttributes, tooltipPosition, activeTooltipMsg, onMenuMouseMove, onMenuMouseLeave } =
        useDropdownContextMenuTooltipController();

    const titleModalLoading = useMemo(() => {
        if (isLoadingWorkOrder) {
            return `Выгрузка ${isFrameContract ? 'Наряд-Заказа' : 'Расчета по договору'}`;
        }

        return 'Выгрузка накопительной ведомости по Наряд-Заказу';
    }, [isLoadingWorkOrder, isFrameContract]);

    return (
        <>
            <DropdownContext
                {...propsDropdown}
                items={menuItems}
                groups={workOrderKebabMenuGroups}
                btnView="ghost"
                btnSize="s"
                btnIconSize="s"
                getItemAttributesHandler={getMenuItemAttributes}
                onMouseLeaveHandler={onMenuMouseLeave}
                onMouseMoveHandler={onMenuMouseMove}
            />
            {isCreateWorkOrderChangeOpened && (
                <BimWorkOrderChangeCreateOrEdit
                    contractStatus={statusContract}
                    isCreating
                    isFrameContract={isFrameContract}
                    onClose={closeCreatingWorkOrderChangeHandler}
                    workOrderId={Number(orderId)}
                    projectData={projectData}
                    refreshProjectData={refreshProjectData}
                />
            )}
            {DeleteOrderErrorHandler}
            {DeleteOrderConfirmDialog}
            {activeTooltipMsg && (
                <Tooltip
                    size="s"
                    direction="upCenter"
                    position={tooltipPosition}
                    isInteractive={false}
                    style={{ zIndex: 2 }}
                >
                    {activeTooltipMsg}
                </Tooltip>
            )}
            <ErrorHandlingModal
                titleText="Выгрузка наряд-заказа"
                isSubmitted={isSubmittedDownload}
                statusRequest={workOrderAsExcelStatus}
                dataRequest={workOrderAsExcelData}
                errorsRequest={workOrderAsExcelError}
                handleCloseModalError={handleCloseModalErrorDownload}
                handleCreateReport={handleCreateReportDownload}
            />
            {(isLoadingWorkOrder || isLoadingBimExportWorkOrderPrices) && (
                <ModalLoading
                    title={titleModalLoading}
                    onClose={isLoadingWorkOrder ? workOrderAsExcelAbort : exportWorkOrderPricesAbort}
                />
            )}
        </>
    );
};

BimWorkOrderKebabMenu.displayName = 'BimWorkOrderKebabMenu';
